import React, { useState } from "react";
import { App, Alert, Button, Input, Spin, Table, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdOutlineSearch } from "react-icons/md";
import Card from "../../components/controls/Card";
import AppControl from "../../components/";
import {
  searchPincodes,
  fetchFromPostalApi,
  savePincodesBulk,
  clearSearch,
} from "../../store/slice/pincodes";

const PREVIEW_COLUMNS = [
  { title: "Name", dataIndex: "name", key: "name" },
  { title: "Branch Type", dataIndex: "branchType", key: "branchType" },
  { title: "District", dataIndex: "district", key: "district" },
  { title: "Division", dataIndex: "division", key: "division" },
  { title: "State", dataIndex: "state", key: "state" },
];

function PincodeAdd() {
  const [pincode, setPincode] = useState("");
  const [status, setStatus] = useState(null); // "exists" | "ready" | "not-found"

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const { isSearching, isFetchingExternal, isLoading, searchResults, externalResults } =
    useSelector((state) => state.pincodes);

  const handleSearch = async () => {
    if (!/^\d{6}$/.test(pincode)) {
      message.warning("Please enter a valid 6-digit pincode.");
      return;
    }

    dispatch(clearSearch());
    setStatus(null);

    const searchAction = await dispatch(searchPincodes(pincode));
    if (searchPincodes.fulfilled.match(searchAction)) {
      const results = searchAction.payload.data ?? [];
      if (results.length > 0) {
        setStatus("exists");
        return;
      }
    }

    // Not found locally — fetch from postal API
    const externalAction = await dispatch(fetchFromPostalApi(pincode));
    if (fetchFromPostalApi.fulfilled.match(externalAction)) {
      setStatus("ready");
    } else {
      setStatus("not-found");
    }
  };

  const handleImport = async () => {
    const resultAction = await dispatch(savePincodesBulk(externalResults));
    if (savePincodesBulk.fulfilled.match(resultAction)) {
      message.success(resultAction.payload.message);
      navigate("/pincodes");
    } else {
      message.error(resultAction.error.message);
    }
  };

  const isBusy = isSearching || isFetchingExternal;

  return (
    <Card title="ADD PINCODE">
      <div className="max-w-md mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Pincode <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          <Input
            value={pincode}
            maxLength={6}
            placeholder="e.g. 723121"
            onChange={(e) => {
              setPincode(e.target.value.replace(/\D/g, ""));
              setStatus(null);
              dispatch(clearSearch());
            }}
            onPressEnter={handleSearch}
            status={status === "not-found" ? "error" : ""}
          />
          <Button
            type="primary"
            icon={<MdOutlineSearch size="1.1em" />}
            loading={isBusy}
            onClick={handleSearch}
          >
            Search
          </Button>
        </div>
      </div>

      {isBusy && (
        <div className="flex items-center gap-2 text-gray-500 mb-4">
          <Spin size="small" />
          <span>{isSearching ? "Checking existing pincodes…" : "Fetching from postal API…"}</span>
        </div>
      )}

      {status === "exists" && (
        <Alert
          type="warning"
          showIcon
          message={`Pincode ${pincode} already exists in the system.`}
          className="mb-4 max-w-lg"
        />
      )}

      {status === "not-found" && (
        <Alert
          type="error"
          showIcon
          message={`No post offices found for pincode ${pincode}.`}
          className="mb-4 max-w-lg"
        />
      )}

      {status === "ready" && externalResults.length > 0 && (
        <>
          <Alert
            type="info"
            showIcon
            message={
              <>
                Found <Tag color="blue">{externalResults.length}</Tag> post office(s) for pincode{" "}
                <strong>{pincode}</strong>. Review and import below.
              </>
            }
            className="mb-4"
          />
          <Table
            size="small"
            rowKey="name"
            columns={PREVIEW_COLUMNS}
            dataSource={externalResults}
            pagination={false}
            className="mb-6"
          />
          <div className="border-t-2 border-neutral-100 pt-4">
            <AppControl
              control="button"
              type="button"
              name={`Import ${externalResults.length} Record(s)`}
              size="md"
              color="orange"
              loading={isLoading}
              onClick={handleImport}
            />
            <AppControl
              control="button"
              type="button"
              name="Cancel"
              size="md"
              color="blue"
              onClick={() => navigate("/pincodes")}
            />
          </div>
        </>
      )}

      {status === null && !isBusy && (
        <div className="border-t-2 border-neutral-100 pt-4">
          <AppControl
            control="button"
            type="button"
            name="Cancel"
            size="md"
            color="blue"
            onClick={() => navigate("/pincodes")}
          />
        </div>
      )}
    </Card>
  );
}

export default PincodeAdd;
